// components/DoctorContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { message } from 'antd';
import API from '../config/api';

interface DoctorSettings {
  receiptHeader: string;
  receiptFooter: string;
  clinicName: string;
  doctorTitle: string;
  reminderDisplayName?: string;
  reminderWhatsAppNumber?: string;
  clinicAddress: string;
  clinicPhone: string;
  logoUrl?: string;
  consultationFee?: number;
  revisitFee?: number;
  estisharaFee?: number;
  urgentFee?: number;
  referralSources?: string[];
  diagramType?: 'none' | 'dental' | 'body';
  printSettings?: {
    paperSize: 'a4' | 'a5' | 'custom';
    marginTop: number;
    marginLeft: number;
    marginRight: number;
    showHeader: boolean;
    showFooter: boolean;
    showPatientInfo: boolean;
    customPaperWidth?: number;
    customPaperHeight?: number;
    printLocale?: 'en' | 'ar';
    physicianPrintOrientation?: 'landscape' | 'portrait';
    useOwnerPrintSettings?: boolean;
    prescriptionCustomCss?: string;
    prescriptionCustomHtml?: string;
    prescriptionHeader?: {
      enabled: boolean;
      leftLines: string[];
      centerBadgeBase64?: string;
      centerBadgeSize?: number;
      centerBadgeWidth?: number;
      centerBadgeHeight?: number;
      headerHeight?: number;
      rightLines: string[];
    };
    prescriptionFooter?: {
      enabled: boolean;
      lines: string[];
    };
  };
}

interface DoctorContextType {
  settings: DoctorSettings;
  loading: boolean;
  doctorId: string | null;
  doctorName: string;
  fetchSettings: () => Promise<void>;
  updateSettings: (settings: DoctorSettings) => Promise<boolean>;
}

const defaultSettings: DoctorSettings = {
  receiptHeader: '',
  receiptFooter: '',
  clinicName: '',
  doctorTitle: '',
  reminderDisplayName: '',
  reminderWhatsAppNumber: '',
  clinicAddress: '',
  clinicPhone: '',
  consultationFee: 0,
  revisitFee: 0,
  estisharaFee: 0,
  urgentFee: 0,
  referralSources: [],
  diagramType: 'none',
  printSettings: {
    paperSize: 'a4',
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    showHeader: true,
    showFooter: true,
    showPatientInfo: true,
    physicianPrintOrientation: 'landscape',
  }
};

const DoctorContext = createContext<DoctorContextType | undefined>(undefined);

// API endpoints
let endpoint = `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS}`;

// const API_BASE_URL = process.env.REACT_APP_API_URL || `'http://localhost:7000'`;
// const DOCTOR_SETTINGS_ENDPOINT = (doctorId: string) => `/api/doctors/${doctorId}/settings`;

export const DoctorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<DoctorSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(false);
  const [doctorName, setDoctorName] = useState<string>('');

  // Always read fresh from localStorage so we never use a stale closure value
  const getDoctorId = () => localStorage.getItem('doctorId');
  const doctorId = getDoctorId();

  // Fetch doctor's settings
  const fetchSettings = async (): Promise<void> => {
    const doctorId = getDoctorId();
    if (!doctorId) {
      console.log('No doctor ID found in localStorage');
      return;
    }

    try {
      setLoading(true);
      console.log(`Fetching doctor settings for ${doctorId}`);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(doctorId!)}`);

      // The API returns { message, doctor_name, settings }
      if (response.data && response.data.settings) {
        console.log('Doctor settings retrieved:', response.data.settings);
        if (response.data.doctor_name) {
          setDoctorName(response.data.doctor_name);
        }
        const backendPS = response.data.settings.printSettings || {};
        const restored: DoctorSettings = {
          ...response.data.settings,
          printSettings: backendPS,
        };
        setSettings(restored);
        // Print settings are now persisted by the backend. Remove the legacy
        // per-browser copy so stale CSS/HTML cannot override another device's save.
        localStorage.removeItem(`printSettingsExtra_${doctorId}`);
      } else {
        console.log('No settings found or unexpected response format');
      }
    } catch (error) {
      console.error('Error fetching doctor settings:', error);
      // Don't show error message to user as this is likely first-time access
      // and we don't want to confuse them
    } finally {
      setLoading(false);
    }
  };

  // Update doctor's settings
  const updateSettings = async (newSettings: DoctorSettings): Promise<boolean> => {
    const doctorId = getDoctorId();
    if (!doctorId) {
      message.error('Doctor ID not found. Please log in again.');
      return false;
    }

    try {
      setLoading(true);
      console.log(`Updating doctor settings for ${doctorId}:`, newSettings);

      const response = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(doctorId)}`,
        newSettings
      );
      if (response.data && response.data.settings) {
        // The backend response is authoritative for persisted print settings.
        localStorage.removeItem(`printSettingsExtra_${doctorId}`);
        const merged: DoctorSettings = {
          ...newSettings,
          ...response.data.settings,
          printSettings: {
            ...newSettings.printSettings,
            ...response.data.settings.printSettings,
          },
        };
        setSettings(merged);
        message.success('Settings saved successfully');
        return true;
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load settings on initial mount
  useEffect(() => {
    fetchSettings();
  }, [doctorId]);

  const value = {
    settings,
    loading,
    doctorId,
    doctorName,
    fetchSettings,
    updateSettings
  };

  return (
    <DoctorContext.Provider value={value}>
      {children}
    </DoctorContext.Provider>
  );
};

export const useDoctorContext = () => {
  const context = useContext(DoctorContext);
  if (context === undefined) {
    throw new Error('useDoctorContext must be used within a DoctorProvider');
  }
  return context;
};
